import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { modalIds } from 'components/modal/Modal.constants';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import useGuestReviewTemplateMutations from 'components/domain/review/useGuestReviewTemplateMutations';
import { GuestReviewTemplate } from 'models/automation/Reviews';
import { GUEST_REVIEW_TEMPLATES_BASE_QUERY_KEY } from 'components/domain/review/useFetchGuestReviewTemplates';

const useDeleteReviewTemplateModal = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { openConfirmModal, closeModal } = useAppModal();
  const { deleteReviewTemplate } = useGuestReviewTemplateMutations();

  const handleDeleteReviewTemplate = async (
    reviewTemplate: GuestReviewTemplate,
  ) => {
    try {
      await deleteReviewTemplate({ uid: reviewTemplate.uid });
      queryClient.invalidateQueries({
        queryKey: [GUEST_REVIEW_TEMPLATES_BASE_QUERY_KEY],
      });
      notifySuccess(
        t('pageAutomations.reviews.guestReviewTemplates.deleteModal.success'),
      );
      closeModal(modalIds.confirm);
    } catch (error) {
      notifyError(
        t('pageAutomations.reviews.guestReviewTemplates.deleteModal.error'),
      );
    }
  };

  const openDeleteReviewTemplateModal = (
    reviewTemplate: GuestReviewTemplate,
  ) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="pageAutomations.reviews.guestReviewTemplates.deleteModal.description"
          values={{ title: reviewTemplate.title }}
          components={{ br: <br /> }}
        />
      ),
      confirmColor: 'danger',
      confirmLabel: t('common.delete'),
      onConfirmAsync: async () => handleDeleteReviewTemplate(reviewTemplate),
      title: t(
        'pageAutomations.reviews.guestReviewTemplates.deleteModal.title',
        { title: reviewTemplate.title },
      ),
    });
  };

  return {
    openDeleteReviewTemplateModal,
  };
};

export default useDeleteReviewTemplateModal;
